import { Activity, Download } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { exportarCsv } from '@/lib/export';
import { formatNumber } from '@/lib/utils';

import { ConsolidadoRic } from '../components/consolidado-ric';
import { RicNav } from '../components/ric-nav';
import { useRicVisual } from '../hooks/use-ric';
import { ANIOS, MESES } from '../lib/ric-format';

/** Mes y año por defecto: el mes anterior (los RIC se registran con rezago). */
function periodoInicial(): { anio: number; mes: number } {
  const hoy = new Date();

  return { anio: hoy.getFullYear(), mes: hoy.getMonth() + 1 };
}

/**
 * RIC — Reporte visual (`/monitoreo/ric/visual`).
 *
 * Puerto de `Reportes::ric_visual`: consolidado mensual **por tanque**, con una
 * tabla de días (1..N) y el desglose de ingresos, salidas, teórica, físico y
 * diferencia. Los tanques sin movimiento se muestran con los días en cero.
 */
export function RicVisualPage() {
  useDocumentTitle('RIC · Reporte visual');

  const inicial = periodoInicial();
  const [anio, setAnio] = useState(inicial.anio);
  const [mes, setMes] = useState(inicial.mes);

  const visual = useRicVisual(anio, mes);
  const bloques = visual.data?.tanks ?? [];

  const exportar = () => {
    if (bloques.length === 0) return;

    const encabezados = [
      'Tanque',
      'Producto',
      'Día',
      'Previo',
      'Ingresos',
      'Salidas',
      'Teórica',
      'Físico',
      'Agua',
      'Físico sin agua',
      'Retornos',
      'Diferencia',
      'Recibido',
    ];

    const filas = bloques.flatMap((bloque) =>
      bloque.rows.map((fila) => [
        bloque.tank_label,
        bloque.product_label,
        fila.day,
        fila.previous.toFixed(3),
        fila.incomes.toFixed(3),
        fila.outcomes.toFixed(3),
        fila.logical.toFixed(3),
        fila.physical.toFixed(3),
        fila.water_gl.toFixed(3),
        fila.physical_no_water.toFixed(3),
        fila.returns.toFixed(3),
        fila.difference.toFixed(3),
        fila.received.toFixed(3),
      ]),
    );

    exportarCsv(`ric-visual-${anio}-${String(mes).padStart(2, '0')}`, encabezados, filas);
  };

  const totalIngresos = bloques.reduce((total, bloque) => total + bloque.totals.incomes, 0);
  const totalSalidas = bloques.reduce((total, bloque) => total + bloque.totals.outcomes, 0);
  const totalDiferencia = bloques.reduce((total, bloque) => total + bloque.totals.difference, 0);
  const totalRecibido = bloques.reduce((total, bloque) => total + bloque.totals.received, 0);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">
            Monitoreo y control
          </p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <Activity className="size-6 text-brand-500" aria-hidden="true" />
            RIC · Reporte visual
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            Consolidado mensual por tanque, con la diferencia diaria entre lo teórico y lo medido.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <RicNav active="visual" />
          <Button
            size="sm"
            variant="outline"
            leftIcon={<Download className="size-3.5" aria-hidden="true" />}
            onClick={exportar}
            disabled={bloques.length === 0}
          >
            Exportar
          </Button>
        </div>
      </header>

      <SectionCard
        title="Período"
        description={
          visual.data?.company_name
            ? `Informe de ${visual.data.company_name}`
            : 'Selecciona el mes a consolidar'
        }
      >
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-[11px] font-medium text-ink-500">
            Mes
            <Select
              value={String(mes)}
              onChange={(event) => setMes(Number(event.target.value))}
              className="h-10 rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
            >
              {MESES.map((nombre, indice) => (
                <option key={nombre} value={String(indice + 1)}>
                  {nombre}
                </option>
              ))}
            </Select>
          </label>

          <label className="flex flex-col gap-1 text-[11px] font-medium text-ink-500">
            Año
            <Select
              value={String(anio)}
              onChange={(event) => setAnio(Number(event.target.value))}
              className="h-10 rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
            >
              {ANIOS.map((valor) => (
                <option key={valor} value={String(valor)}>
                  {valor}
                </option>
              ))}
            </Select>
          </label>

          <Button
            size="sm"
            variant="outline"
            onClick={() => void visual.refetch()}
            loading={visual.isRefetching}
          >
            Consultar
          </Button>

          <span className="pb-2.5 text-xs text-ink-500">
            {visual.data
              ? `Del ${visual.data.start} al ${visual.data.end} · ${visual.data.daysInMonth} días`
              : ''}
          </span>
        </div>
      </SectionCard>

      {visual.isError ? (
        <Alert variant="error" title="No se pudo cargar el consolidado">
          <div className="flex flex-wrap items-center gap-3">
            <span>Verifica el período e inténtalo nuevamente.</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void visual.refetch()}
              loading={visual.isRefetching}
            >
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {bloques.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-card border border-ink-100 bg-white px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
              Ingresos del mes
            </p>
            <p className="mt-1 font-mono text-lg font-bold tabular-nums text-ink-900">
              {formatNumber(totalIngresos)} gal
            </p>
          </div>
          <div className="rounded-card border border-ink-100 bg-white px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
              Salidas del mes
            </p>
            <p className="mt-1 font-mono text-lg font-bold tabular-nums text-ink-900">
              {formatNumber(totalSalidas)} gal
            </p>
          </div>
          <div className="rounded-card border border-ink-100 bg-white px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
              Diferencia acumulada
            </p>
            <p
              className={
                totalDiferencia < 0
                  ? 'mt-1 font-mono text-lg font-bold tabular-nums text-rose-600'
                  : 'mt-1 font-mono text-lg font-bold tabular-nums text-ink-900'
              }
            >
              {formatNumber(totalDiferencia)} gal
            </p>
          </div>
          <div className="rounded-card border border-ink-100 bg-white px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
              Recibido en tanques
            </p>
            <p className="mt-1 font-mono text-lg font-bold tabular-nums text-ink-900">
              {formatNumber(totalRecibido)} gal
            </p>
          </div>
        </div>
      ) : null}

      <SectionCard
        title="Consolidado por tanque"
        description="Cada pestaña es un tanque; los días sin toma aparecen en cero"
      >
        <ConsolidadoRic
          data={visual.data}
          bloques={bloques}
          loading={visual.isPending}
          tituloBloque="Tanques"
        />
      </SectionCard>
    </div>
  );
}
